// Sound design for the reel — sequenced cinematic score (synthesized, ~22s).
// Multiple "stings" timed to the visual scenes.

let ctx = null;
let masterGain = null;
let started = false;

export function startReelAudio({ duration = 22000 } = {}) {
  if (started) return;
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    ctx = new AudioCtx();
    if (ctx.state === 'suspended') ctx.resume();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.0001;
    masterGain.connect(ctx.destination);
    started = true;

    const t0 = ctx.currentTime;
    const tEnd = t0 + duration / 1000;

    // Master envelope
    masterGain.gain.setValueAtTime(0.0001, t0);
    masterGain.gain.exponentialRampToValueAtTime(0.55, t0 + 0.4);
    masterGain.gain.setValueAtTime(0.55, tEnd - 0.6);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, tEnd);

    // ---- Continuous low drone (whole duration) ----
    const droneFilter = ctx.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.setValueAtTime(220, t0);
    droneFilter.frequency.exponentialRampToValueAtTime(1800, tEnd - 1);
    droneFilter.Q.value = 1.1;
    droneFilter.connect(masterGain);

    const droneFreqs = [55, 82.41, 110]; // A2, E3, A3
    droneFreqs.forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = i % 2 === 0 ? 'sawtooth' : 'triangle';
      o.frequency.value = f;
      o.detune.value = (i - 1) * 7;
      const g = ctx.createGain();
      g.gain.value = 0.08;
      // Slow LFO vibrato
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.15 + i * 0.05;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 4;
      lfo.connect(lfoG).connect(o.detune);
      o.connect(g).connect(droneFilter);
      o.start(t0);
      o.stop(tEnd + 0.1);
      lfo.start(t0);
      lfo.stop(tEnd + 0.1);
    });

    // ---- Scene "stings" timed to visual cuts ----
    // helper: noise burst
    const noiseBuffer = (() => {
      const buf = ctx.createBuffer(1, ctx.sampleRate * 1, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      return buf;
    })();

    const noiseHit = (at, cutoffStart, cutoffEnd, gain = 0.3, dur = 0.4) => {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuffer;
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.setValueAtTime(cutoffStart, t0 + at);
      f.frequency.exponentialRampToValueAtTime(cutoffEnd, t0 + at + dur);
      f.Q.value = 1.4;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(gain, t0 + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
      src.connect(f).connect(g).connect(masterGain);
      src.start(t0 + at);
      src.stop(t0 + at + dur + 0.05);
    };

    const subThump = (at, fStart = 160, fEnd = 40, gain = 0.85, dur = 0.6) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(fStart, t0 + at);
      o.frequency.exponentialRampToValueAtTime(fEnd, t0 + at + dur);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(gain, t0 + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
      o.connect(g).connect(masterGain);
      o.start(t0 + at);
      o.stop(t0 + at + dur + 0.05);
    };

    const chordHit = (at, freqs, gain = 0.15, dur = 1.8) => {
      freqs.forEach((f) => {
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.value = f;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t0 + at);
        g.gain.exponentialRampToValueAtTime(gain, t0 + at + 0.3);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
        o.connect(g).connect(masterGain);
        o.start(t0 + at);
        o.stop(t0 + at + dur + 0.05);
      });
    };

    // Countdown beeps (3, 2, 1)
    [0.2, 1.0, 1.8].forEach((at) => {
      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.value = 880;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(0.18, t0 + at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.12);
      o.connect(g).connect(masterGain);
      o.start(t0 + at);
      o.stop(t0 + at + 0.15);
    });

    // Scene transitions
    noiseHit(2.2, 400, 5500, 0.45, 0.7); // whoosh into title
    subThump(2.5, 180, 35, 0.95, 0.7);
    chordHit(2.6, [220, 277.18, 329.63], 0.13, 2.6); // A min: A3 C#4 E4

    noiseHit(5.4, 600, 6500, 0.4, 0.5);
    subThump(5.6, 160, 38, 0.85, 0.55);

    // Rapid-fire cuts in capability section
    [9.0, 9.5, 10.0, 10.5, 11.0, 11.5, 12.0].forEach((at) =>
      subThump(at, 220, 60, 0.35, 0.18)
    );

    // Brand showcase percussive ticks
    [12.8, 13.4, 14.0, 14.6, 15.2, 15.8].forEach((at) => {
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = 1200;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(0.12, t0 + at + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.08);
      o.connect(g).connect(masterGain);
      o.start(t0 + at);
      o.stop(t0 + at + 0.1);
    });

    // Stats reveal — bell-like tones
    [16.8, 17.6, 18.4].forEach((at, i) => {
      const fs = [523.25, 659.25, 783.99][i];
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = fs;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(0.18, t0 + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 1.4);
      o.connect(g).connect(masterGain);
      o.start(t0 + at);
      o.stop(t0 + at + 1.5);
    });

    // Big finale: chord swell + sub
    chordHit(19.6, [110, 164.81, 220, 329.63, 440], 0.16, 2.4);
    subThump(19.8, 180, 30, 1.0, 1.4);

    setTimeout(() => _hardStop(), duration + 600);
  } catch (e) {
    console.error('[reelAudio] failed', e);
    started = false;
  }
}

function _hardStop() {
  if (!ctx) return;
  try { ctx.close(); } catch {}
  ctx = null;
  masterGain = null;
  started = false;
}

export function stopReelAudio() {
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    if (masterGain) {
      masterGain.gain.cancelScheduledValues(now);
      masterGain.gain.setValueAtTime(masterGain.gain.value, now);
      masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
    }
    setTimeout(_hardStop, 300);
  } catch {
    _hardStop();
  }
}
